import axios from 'axios';

import { WaitlistAnswers } from '@/modules/Pages/WaitlistPage/waitlistOptions';
import { AwsConfigApiGateway } from '@/utils/config/api';

export type WaitlistFunnelEventType = 'landing_view' | 'form_started' | 'step_answered' | 'form_submitted';

const SESSION_KEY = 'churnsignal_waitlist_session_v1';
const DRAFT_KEY = 'churnsignal_waitlist_draft_v1';
const LANDING_VIEWED_PREFIX = 'churnsignal_landing_viewed_v1:';
const FORM_STARTED_KEY = 'churnsignal_waitlist_form_started_v1';

export type WaitlistDraft = {
  sessionId: string;
  stepIndex: number;
  answers: WaitlistAnswers;
  updatedAt: string;
};

function createId() {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `wl_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

export function getWaitlistSessionId(): string {
  try {
    const existing = localStorage.getItem(SESSION_KEY);
    if (existing) return existing;
    const id = createId();
    localStorage.setItem(SESSION_KEY, id);
    return id;
  } catch {
    return createId();
  }
}

export function readWaitlistDraft(): WaitlistDraft | null {
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    if (!raw) return null;
    const draft = JSON.parse(raw) as WaitlistDraft;
    if (!draft?.answers || typeof draft.stepIndex !== 'number') return null;
    return draft;
  } catch {
    return null;
  }
}

export function saveWaitlistDraft(stepIndex: number, answers: WaitlistAnswers) {
  const draft: WaitlistDraft = {
    sessionId: getWaitlistSessionId(),
    stepIndex,
    answers,
    updatedAt: new Date().toISOString(),
  };
  try {
    localStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
  } catch {
    // Ignore quota / private mode failures; funnel POST may still succeed.
  }
  return draft;
}

export function clearWaitlistDraft() {
  try {
    localStorage.removeItem(DRAFT_KEY);
    sessionStorage.removeItem(FORM_STARTED_KEY);
  } catch {
    // ignore
  }
}

type TrackPayload = {
  eventType: WaitlistFunnelEventType;
  stepId?: string;
  stepIndex?: number;
  stepLabel?: string;
  answers?: WaitlistAnswers;
  pagePath?: string;
};

export async function trackWaitlistFunnel(payload: TrackPayload): Promise<void> {
  const adminApiUrl = AwsConfigApiGateway.adminApiUrl;
  if (!adminApiUrl) return;

  const answers = payload.answers;
  const body = {
    eventType: payload.eventType,
    sessionId: getWaitlistSessionId(),
    stepId: payload.stepId ?? '',
    stepIndex: payload.stepIndex == null ? '' : String(payload.stepIndex),
    stepLabel: payload.stepLabel ?? '',
    name: answers?.name?.trim() ?? '',
    email: answers?.email?.trim() ?? '',
    company: answers?.company?.trim() ?? '',
    answersJson: answers ? JSON.stringify(answers) : '',
    pagePath: payload.pagePath ?? (typeof window !== 'undefined' ? window.location.pathname : ''),
  };

  try {
    await axios.post(`${adminApiUrl.replace(/\/$/, '')}/waitlist-funnel`, body, {
      headers: {
        ...(AwsConfigApiGateway.pooledTenantApiKey
          ? { 'x-api-key': AwsConfigApiGateway.pooledTenantApiKey }
          : null),
      },
    });
  } catch (error) {
    console.warn('[WaitlistFunnel] failed to record event', payload.eventType, error);
  }
}

/** Fire once per browser session per path (e.g. `/` or `/landing`). */
export function trackLandingViewOnce(pagePath: string) {
  try {
    const key = `${LANDING_VIEWED_PREFIX}${pagePath}`;
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, '1');
  } catch {
    // Still attempt the network event if sessionStorage is unavailable.
  }
  void trackWaitlistFunnel({ eventType: 'landing_view', pagePath });
}

/** Fire once per waitlist session when the wizard is opened. */
export function trackFormStartedOnce() {
  try {
    if (sessionStorage.getItem(FORM_STARTED_KEY)) return;
    sessionStorage.setItem(FORM_STARTED_KEY, '1');
  } catch {
    // continue
  }
  void trackWaitlistFunnel({ eventType: 'form_started', pagePath: '/waitlist' });
}
